import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PaymentDetails from '../components/payment/PaymentDetails';
import OrderSummary from '../components/payment/OrderSummary';
import VoucherCodeCard from '../components/payment/VoucherCodeCard';
import SupportButton from '../components/SupportButton';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import apiService from '../services/api';
import type { OrderStatusResponse } from '../types/api';
import { analytics } from '../utils/analytics';

const Payment: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('payment');
  const { toasts, showToast, removeToast } = useToast();
  const [orderData, setOrderData] = useState<any>(null);
  const [orderStatus, setOrderStatus] = useState<OrderStatusResponse | null>(null);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [autoOpenWebview, setAutoOpenWebview] = useState(false);
  const autoOpenedRef = useRef(false);

  const fetchOrderStatus = useCallback(async (reference: string) => {
    try {
      const status = await apiService.getOrderStatus(reference);
      setOrderStatus(status);
      return status;
    } catch (error) {
      console.error('Failed to fetch order status:', error);
      return null;
    }
  }, []);

  const generatePaymentURL = useCallback(async (reference: string) => {
    setPaymentLoading(true);
    try {
      const result = await apiService.generatePaymentURL(reference);

      // Mosan WebView session: backend rebuilt the partner-redirect URL
      // (same flow as initial CreateOrder). Hand off directly so Mosan
      // intercepts. Skips loading the in-page payment webview which
      // would otherwise double-wrap Mosan's own UI.
      if (result.mosan_redirect_url) {
        window.location.href = result.mosan_redirect_url;
        return;
      }

      setOrderData((prev: any) => ({
        ...prev,
        paymentUrl: result.payment_url,
      }));
      if (result.expires_at) {
        setExpiryTime(new Date(result.expires_at));
      }
      analytics.paymentStarted(reference);
      // Refresh status to get updated payment_url and expiry
      fetchOrderStatus(reference);
    } catch (error) {
      const msg = error instanceof Error ? error.message : t('generate_failed');
      showToast(msg, 'error');
    } finally {
      setPaymentLoading(false);
    }
  }, [fetchOrderStatus, showToast]);

  useEffect(() => {
    if (!orderId) {
      navigate('/');
      return;
    }

    fetchOrderStatus(orderId).then((status) => {
      if (!status) {
        navigate('/');
        return;
      }
      setOrderData({
        game: { name: status.product_name, image: status.product_image || '' },
        product: status.product_item,
        price: status.amount,
        phoneNumber: status.phone_number || '-',
        userData: status.user_data || {},
        payment: 'Mosan',
        paymentUrl: status.payment_url || '',
        reference: orderId,
        status: status.status,
        paymentStatus: status.payment_status,
        createdAt: status.created_at,
      });
      if (status.expires_at) {
        setExpiryTime(new Date(status.expires_at));
      }
    });
  }, [orderId, navigate, fetchOrderStatus]);

  // Auto-generate payment URL when coming from checkout
  useEffect(() => {
    if (!orderStatus || !orderId || autoOpenedRef.current) return;
    if (orderStatus.payment_status !== 'pending') return;
    if (orderStatus.payment_url) return;

    const fromCheckout = sessionStorage.getItem('fromCheckout') === orderId;
    if (!fromCheckout) return;

    autoOpenedRef.current = true;
    sessionStorage.removeItem('fromCheckout');
    setAutoOpenWebview(true);
    generatePaymentURL(orderId);
  }, [orderStatus, orderId, generatePaymentURL]);

  // Polling every 10 seconds
  useEffect(() => {
    if (!orderId) return;

    const interval = setInterval(() => {
      fetchOrderStatus(orderId);
    }, 10000);

    return () => clearInterval(interval);
  }, [orderId, fetchOrderStatus]);

  // Update orderData when status changes
  useEffect(() => {
    if (orderStatus && orderData) {
      setOrderData((prev: any) => ({
        ...prev,
        game: {
          name: orderStatus.product_name,
          image: orderStatus.product_image || prev.game.image,
        },
        product: orderStatus.product_item,
        price: orderStatus.amount,
        phoneNumber: orderStatus.phone_number || '-',
        userData: orderStatus.user_data || {},
        paymentUrl: orderStatus.payment_url || prev.paymentUrl,
        status: orderStatus.status,
        paymentStatus: orderStatus.payment_status,
        invoiceId: orderStatus.invoice_id,
        sn: orderStatus.sn,
      }));
      if (orderStatus.expires_at) {
        setExpiryTime(new Date(orderStatus.expires_at));
      }
    }
  }, [orderStatus]);

  const handleManualRefresh = async () => {
    if (!orderId) return;
    setIsRefreshing(true);
    await fetchOrderStatus(orderId);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handlePayNow = async () => {
    if (!orderId) return;
    await generatePaymentURL(orderId);
  };

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Status resolution: payment_status is the source-of-truth until the
  // buyer actually pays. Only after payment_status='paid' do we fall
  // through to the fulfillment status (success/failed/processing).
  // Otherwise the page rendered "Processing" for an already-expired
  // order because the row's `status` column keeps reading 'pending'
  // until the payment lifecycle terminates.
  const displayStatus = orderStatus ? (
    orderStatus.status === 'refunded' ? 'Refunded' :
    orderStatus.payment_status === 'paid'
      ? (orderStatus.status === 'success' ? 'Success'
          : orderStatus.status === 'failed' ? 'Failed'
          : 'Processing')
      : orderStatus.payment_status === 'pending' ? 'Pending'
      : orderStatus.payment_status === 'expired' ? 'Expired'
      : orderStatus.payment_status === 'failed' ? 'Failed'
      : 'Processing'
  ) : 'Pending';

  const statusColor =
    displayStatus === 'Success' ? 'text-green-600' :
    displayStatus === 'Processing' ? 'text-blue-600' :
    displayStatus === 'Failed' ? 'text-red-600' :
    displayStatus === 'Expired' ? 'text-gray-700' :
    displayStatus === 'Refunded' ? 'text-yellow-600' :
    'text-gray-600';

  const isPending = displayStatus === 'Pending';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
        {/* Status Banner */}
        <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">
                {t('status_label')}: <span className={statusColor}>{t(`status_${displayStatus.toLowerCase()}`)}</span>
              </h3>
            </div>
            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:brightness-90 disabled:bg-gray-400 transition-colors"
            >
              {isRefreshing ? t('refreshing') : t('refresh')}
            </button>
          </div>
        </div>

        {/* 70/30 Layout */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Section (70%) - Always show PaymentDetails */}
          <div className="w-full lg:w-[70%]">
            {paymentLoading ? (
              <div className="bg-white rounded-lg shadow-md p-6 text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">{t('generating')}</p>
              </div>
            ) : isPending ? (
              <PaymentDetails
                paymentUrl={orderData.paymentUrl}
                price={orderData.price}
                expiryTime={expiryTime}
                onPayNow={!orderData.paymentUrl ? handlePayNow : undefined}
                autoOpen={autoOpenWebview}
                onAutoOpened={() => setAutoOpenWebview(false)}
                onPaymentDone={() => {
                  if (orderId) {
                    analytics.paymentCompleted(orderId);
                    fetchOrderStatus(orderId);
                  }
                }}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 text-center space-y-4">
                {/* Status Icon */}
                <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
                  displayStatus === 'Success' ? 'bg-green-100' :
                  displayStatus === 'Processing' ? 'bg-blue-100' :
                  displayStatus === 'Failed' ? 'bg-red-100' :
                  displayStatus === 'Expired' ? 'bg-gray-200' :
                  'bg-yellow-100'
                }`}>
                  <span className="text-4xl">
                    {displayStatus === 'Success' ? '✓' :
                     displayStatus === 'Processing' ? '⏳' :
                     displayStatus === 'Failed' ? '✕' :
                     displayStatus === 'Expired' ? '⌛' : '↩'}
                  </span>
                </div>

                {/* Status Message */}
                <h3 className="text-xl font-semibold text-gray-800">
                  {displayStatus === 'Success' ? t('title_success') :
                   displayStatus === 'Processing' ? t('title_processing') :
                   displayStatus === 'Failed' ? t('title_failed') :
                   displayStatus === 'Expired' ? t('title_expired') :
                   t('title_refunded')}
                </h3>
                <p className="text-gray-500">
                  {displayStatus === 'Success' && orderStatus?.product_type === 'voucher' ? t('msg_success_voucher') :
                   displayStatus === 'Success' ? t('msg_success_topup') :
                   displayStatus === 'Processing' ? t('msg_processing') :
                   displayStatus === 'Failed' ? t('msg_failed') :
                   displayStatus === 'Expired' ? t('msg_expired') :
                   t('msg_refunded')}
                </p>

                {/* Voucher Code Card — only for voucher-type products on success */}
                {displayStatus === 'Success' && orderStatus?.product_type === 'voucher' && orderStatus.sn && (
                  <VoucherCodeCard
                    code={orderStatus.sn}
                    productName={orderStatus.product_name}
                    productItem={orderStatus.product_item}
                    onCopy={() => showToast(t('voucher_copied'), 'success')}
                  />
                )}

                {/* Topup confirmation — show sn as low-key transaction reference */}
                {displayStatus === 'Success' && orderStatus?.product_type === 'topup' && orderStatus.sn && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-left">
                    <p className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-1">
                      {t('topup_confirmation')}
                    </p>
                    <p className="text-sm text-green-900 font-mono break-all">{orderStatus.sn}</p>
                  </div>
                )}

                {/* Order Number */}
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <p className="text-xs text-gray-500 mb-1">{t('order_number')}</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-sm font-mono text-gray-800 break-all">{orderId}</code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(orderId || '');
                        showToast(t('order_number_copied'), 'success');
                      }}
                      className="text-xs px-2 py-1 text-primary hover:bg-primary hover:text-white border border-primary rounded transition-colors"
                    >
                      {t('copy')}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{t('save_track_hint')}</p>
                </div>

                {/* Back Button */}
                <button
                  onClick={() => window.location.href = '/'}
                  className="mt-2 px-6 py-2 bg-primary text-white rounded-lg hover:brightness-90 font-medium"
                >
                  {t('back_home')}
                </button>
              </div>
            )}
          </div>

          {/* Right Section (30%) - Order Summary */}
          <div className="w-full lg:w-[30%]">
            <OrderSummary orderData={orderData} />
          </div>
        </div>
      </main>

      <Footer />
      <SupportButton />

      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((t) => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => removeToast(t.id)} />
        ))}
      </div>
    </div>
  );
};

export default Payment;
